import { Component } from "@angular/core";
import { AuthService } from "@ponder-core/auth/auth.service";
import { AdminUnauthorizedComponent } from "../admin-unauthorized.component";
import { RoleAndPermissions } from "@ponder-core/auth/role-permissions";
import { ListUsersResponse, SortBy, UserPermissionType, UserRoleAndPermissions, UserStatusType } from "@apiserver-rest/ponder-auth-client-base";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { PonderAuthClient } from "@apiserver-rest/ponder-auth-client";
import { take } from "rxjs";
import { FormatDatePipe } from "@ponder-shared/pipes/format-date.pipe";
import { AvatarUrlPipe } from "@ponder-shared/pipes/avatar-url.pipe";
import { NgClass } from "@angular/common";
import { EditUserDialogComponent } from "./dialog/edit-user-dialog.component";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { ActivatedRoute, Router } from "@angular/router";
import { DeleteUserDialogComponent } from "./dialog/delete-user-dialog.component";
import { UndeleteUserDialogComponent } from "./dialog/undelete-user-dialog.component";

@Component({
    selector: "admin-users",
    imports: [
        LoadingComponent,
        AdminUnauthorizedComponent,
        EditUserDialogComponent,
        DeleteUserDialogComponent,
        UndeleteUserDialogComponent,
        FormatDatePipe,
        AvatarUrlPipe,
        NgClass
    ],
    templateUrl: "./admin-users.component.html"
})
export class AdminUsersComponent {
    public isLoadingCanView: boolean = true;
    public isLoading: boolean = false;
    public loadingSuccess: boolean = false;

    public canView: boolean = false;
    public canEdit: boolean = false;
    public canDelete: boolean = false;

    public users: UserRoleAndPermissions[] = [];
    public totalUsers: number = 0;
    public pageStart: number = 0;
    public pageEnd: number = 0;

    public usernameFilter?: string;
    public emailFilter?: string;

    private readonly pageSize: number = 25;

    public constructor(
        private authService: AuthService, 
        private ponderAuthClient: PonderAuthClient, 
        private events: EventBroker, 
        private route: ActivatedRoute,
        private router: Router) {
        
    }

    public ngOnInit(): void {
        var queryParams = this.route.snapshot.queryParamMap;

        if (queryParams.has("username")) {
            this.usernameFilter = queryParams.get("username") ?? undefined;
        }

        if (queryParams.has("email")) {
            this.emailFilter = queryParams.get("email") ?? undefined;
        }

        this.authService.getRoleAndPermissions().then((roleAndPermissions: RoleAndPermissions | undefined) => {
            this.canView = roleAndPermissions?.hasPermission(UserPermissionType.ViewUsersList) == true;
            this.canEdit = roleAndPermissions?.hasPermission(UserPermissionType.EditUserPermissions) == true;
            this.canDelete = roleAndPermissions?.hasPermission(UserPermissionType.DeleteAccount) == true;
            
            if (this.canView) {
                this.fetchData(0);
            }

            this.isLoadingCanView = false;
        });
    }

    private fetchData(start: number): void {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.loadingSuccess = false;

        this.ponderAuthClient
            .listUsers(SortBy.JoinDate, start, this.pageSize, this.usernameFilter, this.emailFilter)
            .pipe(take(1))
            .subscribe({
                next: (response: ListUsersResponse) => {
                    if (response.success && response.users) {
                        this.users = response.users;
                        this.pageStart = (response.start ?? 0) + 1;
                        this.pageEnd = this.pageStart + this.users.length - 1;
                        this.totalUsers = response.totalUsers ?? 0;
                        this.loadingSuccess = true;
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    public onEditClicked(user: UserRoleAndPermissions): void {
        if (!this.canEdit) {
            return;
        }

        this.events.admin.editUserEvent.emit(user);
    }

    public onDeleteClicked(user: UserRoleAndPermissions): void {
        if (!this.canDelete) {
            return;
        }

        this.events.admin.deleteUserEvent.emit(user);
    }

    public onUndeleteClicked(user: UserRoleAndPermissions): void {
        if (!this.canDelete) {
            return;
        }

        this.events.admin.undeleteUserEvent.emit(user);
    }

    public isDeletedUser(user: UserRoleAndPermissions): boolean {
        return user.user.status == UserStatusType.Deleted;
    }

    public onPrevClicked(): void {
        this.fetchData(Math.max(0, this.pageStart - this.pageSize));
    }

    public onNextClicked(): void {
        this.fetchData(Math.max(0, this.pageStart - 1) + this.pageSize);
    }

    public applyFilter(usernameFilter: string | undefined, emailFilter: string | undefined): void {
        this.usernameFilter = usernameFilter;
        this.emailFilter = emailFilter;

        this.setUrlParameters();
        this.fetchData(0);
    }

    public clearFilters(): void {
        if (this.usernameFilter || this.emailFilter) {
            this.usernameFilter = undefined;
            this.emailFilter = undefined;
    
            this.setUrlParameters();
            this.fetchData(0);
        }
    }

    private setUrlParameters() {
        this.router.navigate([], {
            relativeTo: this.route, 
            queryParams: {
                ...this.usernameFilter && { username: this.usernameFilter },
                ...this.emailFilter && { email: this.emailFilter }
            }
        });
    }
}